
import React, { useState, useEffect, useMemo } from 'react';
import { Quote, AircraftType, Customer, Role, UserAccount } from '../types';
import { queryAgent } from '../services/gemini';

interface StrategyInsightsProps {
  quotes: Quote[];
  aircrafts: AircraftType[];
  customers: Customer[];
  users: UserAccount[];
  currentRole: Role;
}

const StrategyInsights: React.FC<StrategyInsightsProps> = ({ quotes, aircrafts, customers, users, currentRole }) => {
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
   const [activeView, setActiveView] = useState<'OVERVIEW' | 'YIELD' | 'RISK'>('OVERVIEW');

   const yieldMetrics = useMemo(() => {
      if (!Array.isArray(quotes) || !Array.isArray(aircrafts)) {
         return { approvedCount: 0, marginUSD: 0, marginPct: 0, utilization: 0, topCustomer: {} };
      }
      const approved = quotes.filter(q => q.status === 'APPROVED' || q.status === 'SENT');
      const marginUSD = approved.reduce((sum, q) => {
         const cost = (Object.values(q.costs || {}) as unknown[]).reduce<number>((s, v: any) => s + (typeof v === 'number' ? v : 0), 0);
         return sum + (q.totalUSD - cost);
      }, 0);
      const marginPct = approved.length ? (marginUSD / approved.reduce((s, q) => s + q.totalUSD, 0)) * 100 : 0;
      const utilization = aircrafts.length ? (aircrafts.filter(a => a.status === 'SERVICEABLE').length / aircrafts.length) * 100 : 0;
      return { 
         approvedCount: approved.length,
         marginUSD,
         marginPct,
         utilization,
         topCustomer: approved.reduce((top, q) => {
            top[q.customerName] = (top[q.customerName] || 0) + q.totalUSD;
            return top;
         }, {} as Record<string, number>),
      };
   }, [quotes, aircrafts]);

   const dutyAlerts = useMemo(() => {
      if (!Array.isArray(users)) return [];
      return users
         .filter(u => u.role === 'CP')
         .map(u => ({ name: u.name, duty: u.dutyHoursToday || 0, max: u.maxDutyHours || 12 }))
         .filter(p => p.duty >= 0.75 * p.max)
         .sort((a, b) => b.duty - a.duty);
   }, [users]);

   const context = useMemo(() => {
      return JSON.stringify({
         fleetSize: Array.isArray(aircrafts) ? aircrafts.length : 0,
         fleetStatus: Array.isArray(aircrafts) ? aircrafts.map(a => ({ tail: a.tailNumber, status: a.status, hours: a.totalBlockHours, fuelBurn: a.fuelBurnLitersPerHour })) : [],
         activeQuotes: Array.isArray(quotes) ? quotes.length : 0,
         revenuePotential: Array.isArray(quotes) ? quotes.reduce((s, q) => s + q.totalUSD, 0) : 0,
         customers: Array.isArray(customers) ? customers.map(c => ({ name: c.name, bal: c.outstandingBalance, limit: c.creditLimit })) : [],
         pilots: Array.isArray(users) ? users.filter(u => u.role === 'CP').map(u => ({ name: u.name, hours: u.totalFlightHours, duty: u.dutyHoursToday, max: u.maxDutyHours })) : [],
         yieldMetrics,
      });
   }, [quotes, aircrafts, customers, users, yieldMetrics]);

  const runExecutiveAudit = async () => {
    setIsAnalyzing(true);
      const prompt = `Perform EXECUTIVE MODE audit.
      - Yield Synthesis: identify top customers by revenue, margin%, and recommend 2 dynamic pricing levers (surcharge/discount) for next 14 days.
      - Safety Risk Audit: flag pilots >75% duty, and any fleet AOG/MAINT items; prescribe mitigations.
      - Market Intelligence: use googleSearch to check EA fuel/tourism signals and propose surcharge or seasonal promo.
      Output as bullets: Action | Owner | Timeframe.`;

      const result = await queryAgent(prompt, context, undefined, 'EXECUTIVE');
    setAiAnalysis(result || "Uplink Error: Strategy Node Unreachable.");
    setIsAnalyzing(false);
  };

  useEffect(() => {
    if (!aiAnalysis && !isAnalyzing) runExecutiveAudit();
  }, []);

  return (
    <div className="max-w-full mx-auto pb-32 animate-in fade-in space-y-12 h-full flex flex-col">
       <div className="bg-slate-950 p-12 rounded-[4rem] border border-white/10 shadow-2xl relative overflow-hidden shrink-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
             <div>
                <h2 className="text-5xl font-black text-white tracking-tighter uppercase leading-none mb-4">Executive Strategy Node</h2>
                <div className="flex items-center gap-4">
                   <span className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></span>
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Miracle Air Strategic Intelligence Interface • Powered by Gemini 3 Pro</p>
                </div>
             </div>
             <button 
               onClick={runExecutiveAudit}
               disabled={isAnalyzing}
               className={`px-10 py-5 rounded-3xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center gap-4 ${
                 isAnalyzing ? 'bg-slate-800 text-slate-500 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-500 shadow-xl shadow-indigo-900/50'
               }`}
             >
                {isAnalyzing ? (
                   <>
                      <div className="w-4 h-4 border-2 border-slate-600 border-t-transparent rounded-full animate-spin"></div>
                      Synthesizing Assets...
                   </>
                ) : (
                   <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                      Refresh Strategic Audit
                   </>
                )}
             </button>
          </div>
       </div>

       <div className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
             <div className="bg-white rounded-[3.5rem] p-12 border border-slate-100 shadow-sm flex-1 min-h-[600px] flex flex-col">
                <div className="flex justify-between items-center mb-10 pb-6 border-b border-slate-50">
                   <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">AI Generated Strategy Report</h3>
                   <div className="flex bg-slate-50 p-1.5 rounded-2xl gap-2">
                      {['OVERVIEW', 'YIELD', 'RISK'].map(v => (
                        <button key={v} onClick={() => setActiveView(v as any)} className={`px-6 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${activeView === v ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400'}`}>{v}</button>
                      ))}
                   </div>
                </div>

                {isAnalyzing ? (
                   <div className="flex-1 flex flex-col items-center justify-center gap-6 opacity-30">
                      <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-[10px] font-black uppercase tracking-widest animate-pulse">Scanning Operational Matrix...</p>
                   </div>
                ) : (
                   <div className="flex-1 prose prose-slate max-w-none prose-sm font-medium text-slate-600 overflow-y-auto custom-scrollbar pr-6">
                      {aiAnalysis ? (
                         <div className="whitespace-pre-wrap leading-relaxed animate-in fade-in slide-in-from-bottom-4">
                            {aiAnalysis.split('\n').map((line, i) => {
                              if (line.startsWith('###')) return <h4 key={i} className="text-lg font-black text-slate-900 uppercase tracking-tight mt-8 mb-4">{line.replace('###', '')}</h4>;
                              if (line.startsWith('**')) return <strong key={i} className="text-slate-900 font-bold block mt-4">{line}</strong>;
                              return <p key={i} className="mb-2">{line}</p>;
                            })}
                         </div>
                      ) : (
                         <div className="h-full flex flex-col items-center justify-center text-center py-20 grayscale opacity-20">
                            <svg className="w-24 h-24 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                            <p className="text-xl font-black uppercase tracking-widest">Awaiting Command Input</p>
                         </div>
                      )}
                   </div>
                )}
             </div>
          </div>

          <div className="space-y-10">
             <div className="bg-slate-900 rounded-[3.5rem] p-12 text-white shadow-2xl space-y-8 relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform"></div>
                <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-400 border-b border-white/10 pb-6">Executive Risk Heatmap</h4>
                <div className="space-y-6">
                     <RiskMeter label="Fleet Availability" percentage={Math.round((aircrafts.filter(a => a.status === 'SERVICEABLE').length / Math.max(aircrafts.length,1)) * 100)} color="bg-indigo-500" />
                     <RiskMeter label="Pilot Rest Compliance" percentage={dutyAlerts.length ? 100 - Math.min(100, Math.round((dutyAlerts[0].duty / dutyAlerts[0].max) * 100)) : 95} color="bg-emerald-500" />
                     <RiskMeter label="Revenue Conversion" percentage={Math.min(100, Math.round((yieldMetrics.marginPct || 0)))} color="bg-amber-500" />
                     <RiskMeter label="Fuel Cost Exposure" percentage={75} color="bg-rose-500" />
                </div>
                <div className="pt-10">
                   <button className="w-full py-6 bg-white/5 border border-white/10 rounded-[2rem] text-[9px] font-black uppercase tracking-widest hover:bg-white/10 transition-all">Download Board Memo</button>
                </div>
             </div>

             <div className="bg-indigo-600 rounded-[3.5rem] p-12 text-white shadow-2xl relative overflow-hidden">
                <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-white/50 mb-8">Board Directive</h4>
                <p className="text-2xl font-black tracking-tighter leading-tight mb-8">"Deploy Dynamic Pricing: +6% surcharge on low-availability tails; hold 10% discount for Serengeti Safaris mid-week uplift."</p>
                <div className="flex items-center gap-4">
                   <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center font-black text-sm">AI</div>
                   <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest">Generated by Gemini Strategy Node</p>
                </div>
             </div>
          </div>
       </div>
    </div>
  );
};

const RiskMeter: React.FC<{ label: string; percentage: number; color: string }> = ({ label, percentage, color }) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest">
       <span className="text-slate-400">{label}</span>
       <span>{percentage}%</span>
    </div>
    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
       <div className={`h-full ${color} transition-all duration-1000`} style={{ width: `${percentage}%` }}></div>
    </div>
  </div>
);

export default StrategyInsights;
